import { shellIcons } from '@dashboard/components/icons/shellIcons'
import { useHostedPagesAccess } from '@dashboard/modules/pages/pagesAccess'
import { useSettingsAccess } from '@dashboard/modules/settings/settingsAccess'
import { ROUTES } from '@dashboard/router/routeNames'
import { usePermissionStore } from '@dashboard/stores/permissionStore'
import { computed } from 'vue'
import type { Component } from 'vue'
import type { RouteLocationNamedRaw } from 'vue-router'

export type SettingsNavItem = {
  name: string
  to: RouteLocationNamedRaw & { name: string }
  icon: Component
  /** Detail routes that keep this item current, such as one integration under Integrations. */
  childRoutes?: string[]
}

/** Project settings: pages about the open project. Labels are the page H1s word for word. */
export const useProjectSettingsNavigation = () => {
  const { canAccess } = usePermissionStore()
  const { allows } = useSettingsAccess()
  const hostedPages = useHostedPagesAccess()

  return computed<SettingsNavItem[]>(() => [
    { name: 'General', to: { name: ROUTES.SETTINGS_GENERAL }, icon: shellIcons.settings },
    { name: 'Website & install', to: { name: ROUTES.SETTINGS_WEBSITE }, icon: shellIcons.codeSquare },
    ...(hostedPages.value
      ? [{ name: 'Page domains', to: { name: ROUTES.SETTINGS_PAGE_DOMAINS }, icon: shellIcons.pages }]
      : []),
    {
      name: 'Integrations',
      to: { name: ROUTES.INTEGRATIONS },
      icon: shellIcons.squaresPlus,
      childRoutes: [ROUTES.INTEGRATION]
    },
    { name: 'Forms & widgets', to: { name: ROUTES.SETTINGS_FORMS }, icon: shellIcons.rectangleGroup },
    { name: 'Privacy & data', to: { name: ROUTES.SETTINGS_PRIVACY }, icon: shellIcons.shieldCheck },
    { name: 'AI context', to: { name: ROUTES.SETTINGS_AI }, icon: shellIcons.sparkles },
    ...(canAccess('manage-collaborators')
      ? [{ name: 'Collaborators', to: { name: ROUTES.SETTINGS_COLLABORATORS }, icon: shellIcons.collaborators }]
      : []),
    ...(canAccess('manage-billing') && allows('perTeamBilling')
      ? [{ name: 'Plan & billing', to: { name: ROUTES.SETTINGS_BILLING }, icon: shellIcons.creditCard }]
      : [])
  ])
}

/**
 * Your account settings: the same for every project. Agency users get a
 * separate Agency group, which also holds Affiliate; everyone else finds
 * Affiliate at the end of their own pages.
 */
export const useAccountSettingsNavigation = () => {
  const { allows } = useSettingsAccess()

  return computed<{ account: SettingsNavItem[]; agency: SettingsNavItem[] }>(() => {
    const isAgency = allows('agency')
    const affiliate = { name: 'Affiliate', to: { name: ROUTES.ACCOUNT_AFFILIATE }, icon: shellIcons.gift }

    return {
      account: [
        { name: 'Profile', to: { name: ROUTES.ACCOUNT_PROFILE }, icon: shellIcons.userCircle },
        { name: 'Sign-in & security', to: { name: ROUTES.ACCOUNT_SECURITY }, icon: shellIcons.lockClosed },
        { name: 'Notifications', to: { name: ROUTES.ACCOUNT_NOTIFICATIONS }, icon: shellIcons.bell },
        { name: 'Preferences', to: { name: ROUTES.ACCOUNT_PREFERENCES }, icon: shellIcons.advanced },
        { name: 'Projects', to: { name: ROUTES.ACCOUNT_PROJECTS }, icon: shellIcons.folder },
        {
          name: 'Organizations',
          to: { name: ROUTES.ACCOUNT_ORGANIZATIONS },
          icon: shellIcons.building,
          childRoutes: [ROUTES.ACCOUNT_ORGANIZATION]
        },
        { name: 'API & AI access', to: { name: ROUTES.ACCOUNT_API }, icon: shellIcons.key },
        ...(allows('legacyBilling')
          ? [{ name: 'Billing', to: { name: ROUTES.ACCOUNT_BILLING }, icon: shellIcons.creditCard }]
          : []),
        ...(isAgency ? [] : [affiliate])
      ],
      agency: isAgency
        ? [
            { name: 'Agency profile', to: { name: ROUTES.AGENCY_PROFILE }, icon: shellIcons.building },
            { name: 'Clients & invitations', to: { name: ROUTES.AGENCY_CLIENTS }, icon: shellIcons.clients },
            affiliate
          ]
        : []
    }
  })
}
